import { Alert, AlertDialog, AlertDialogBody, AlertDialogContent, AlertDialogFooter, AlertDialogHeader, AlertDialogOverlay, AlertIcon, Avatar, Box, Button, Center, CloseButton, Flex, FormControl, FormErrorMessage, FormLabel, IconButton, Select, SimpleGrid, Text, useColorModeValue, useDisclosure } from "@chakra-ui/react";

import { assetRepository, Department, Location, Organization, Vendor } from "api/assetRepository";
import Card from "components/card/Card";
import InputField from "components/fields/InputField";
import { authRepository, User, UserUpdatePayload } from "api/authRepository";

import { useAuth } from "contexts/authContext";
import { jwtDecode } from "jwt-decode";
import { useEffect, useRef, useState } from "react";

import { useLocation, useNavigate } from "react-router-dom";
import { flattenZodErrors, passwordSchema, updateUserSchema } from "validations/Validations";
import { z } from "zod";

import profile from "assets/img/crm/vbz.png";
import { it } from "zod/v4/locales";
import { ArrowBackIcon, ArrowLeftIcon, ChevronLeftIcon } from "@chakra-ui/icons";

const userProfileRequestCache = new Map<string, Promise<User>>();

export default function UserProfile() {


  const [fullName, setFullName] = useState("Loading...");
  const [avatarUrl, setAvatarUrl] = useState(profile);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadedS3Key, setUploadedS3Key] = useState<string>("");
  const [isUploading, setIsUploading] = useState(false);
  const [isDeletingPic, setIsDeletingPic] = useState(false);
  const [userId, setUserId] = useState<string | null>(null);
  const [loggedInUserId, setLoggedInUserId] = useState<string | null>(null);

  const navigate = useNavigate();
  const { role, user: authUser, setUser } = useAuth();
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  const location = useLocation();
  const passedUserId = (location.state as { userId?: string })?.userId || null;


  const [userData, setUserData] = useState<UserUpdatePayload>({
    firstName: "",
    lastName: "",
    email: "",
    userRole: "",
    contactNumber: "",
    orgId: "",
    orgName: "",
    locationId: "",
    location: "",
    departmentId: "",
    departmentName: "",
    vendorId: "",
    isActive: true,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [selectedOrg, setSelectedOrg] = useState<string>("");
  const [selectedLocation, setSelectedLocation] = useState<string>("");
  const [selectedDept, setSelectedDept] = useState<string>("");
  const [selectedVendor, setSelectedVendor] = useState<string>("");

  const isAdmin = role === "Admin";
  const isOrgAdmin = role === "OrgAdmin";
  const isLocationAdmin = role === "LocationAdmin";
  const isDeptUser = role === "DeptUser";
  const isSelf = userId === loggedInUserId;

  const [formData, setFormData] = useState({
    oldPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const [centerAlertVisible, setCenterAlertVisible] = useState(false);
  const [centerAlertMessage, setCenterAlertMessage] = useState("");
  const [centerAlertStatus, setCenterAlertStatus] = useState<"success" | "error">("success");

  const { isOpen, onOpen, onClose } = useDisclosure();
  const cancelRef = useRef<HTMLButtonElement | null>(null);
  const pendingUserRequestRef = useRef<string | null>(null);
  const completedUserRequestRef = useRef<string | null>(null);

  const textColorPrimary = useColorModeValue("secondaryGray.900", "white");
  const textColorSecondary = "secondaryGray.600";

  useEffect(() => {
    if (centerAlertVisible) {
      const timer = setTimeout(() => setCenterAlertVisible(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [centerAlertVisible]);

  // Fetch vendors (for both Admin and Vendor roles)
  useEffect(() => {
    if (!["Admin", "Vendor"].includes(role)) return;

    const loadVendors = async () => {
      const vendorList = await assetRepository.getVendors();
      setVendors(vendorList);
    };

    loadVendors();
  }, [role]);

  // Fetch user info
  useEffect(() => {
    let isMounted = true;

    const fetchUser = async () => {
      let id: string | null = null;
      let localUserId: string | null = null;

      const token = localStorage.getItem("jwt");

      if (token) {
        try {
          const decoded: any = jwtDecode(token);
          localUserId = decoded["id"];
          setLoggedInUserId(localUserId);
        } catch (error) { }
      }

      if (
        (role === "Admin" ||
          role === "OrgAdmin" ||
          role === "LocationAdmin") &&
        passedUserId
      ) {
        id = passedUserId;
      } else {
        id = localUserId;
      }

      if (!id) return;

      const requestKey = `${role || "unknown"}:${id}`;
      const shouldUseContextUser = !passedUserId && Boolean(authUser?.userId) && authUser.userId === id;

      if (
        pendingUserRequestRef.current === requestKey ||
        completedUserRequestRef.current === requestKey
      ) {
        return;
      }

      pendingUserRequestRef.current = requestKey;
      setUserId(id);

      try {
        let user: User;

        if (shouldUseContextUser) {
          user = authUser as User;
        } else {
          const cachedRequest = userProfileRequestCache.get(requestKey);

          if (cachedRequest) {
            user = await cachedRequest;
          } else {
            const requestPromise = authRepository.getUserById(id).finally(() => {
              userProfileRequestCache.delete(requestKey);
            });

            userProfileRequestCache.set(requestKey, requestPromise);
            user = await requestPromise;
          }
        }

        if (!isMounted) return;

        // Always set basic fields
        const basicData = {
          firstName: user.firstName,
          lastName: user.lastName,
          email: user.email,
          userRole: user.userRole,
          contactNumber: user.contactNumber || "",
          isActive: user.isActive,
        };


        let orgs = organizations;

        if (isAdmin && orgs.length === 0) {
          orgs = await assetRepository.getOrganizations();
          setOrganizations(orgs);
        }

        const org = orgs.find((o) => o.orgId === user.orgId);

        let locs: Location[] = [];

        if ((isAdmin || isOrgAdmin) && user.orgId) {
          locs = await assetRepository.getLocations(user.orgId);
          setLocations(locs);
        }

        const loc = locs.find((l) => l.locationId === user.locationId);

        let depts: Department[] = [];

        if ((isAdmin || isOrgAdmin || isLocationAdmin) && loc) {
          depts = loc.departments || [];
          setDepartments(depts);
        }

        const dept = depts.find((d) => d.departmentId === user.departmentId);

        const vendor = vendors.find((v) => v.vendorId === user.vendorId);

        setUserData({
          ...basicData,
          orgId: user.orgId || "",
          orgName: org?.orgName || user.orgName || "",
          locationId: user.locationId || "",
          location: loc?.location || user.location || "",
          departmentId: user.departmentId || "",
          departmentName: dept?.departmentName || user.departmentName || "",
          vendorId: user.vendorId || "",
        });

        setSelectedOrg(user.orgId || "");
        setSelectedLocation(user.locationId || "");
        setSelectedDept(user.departmentId || "");
        setSelectedVendor(user.vendorId || "");

        // Load profile picture (backend returns S3 key in profilePicture)
        if (user.profilePicture) {
          const res = await authRepository.getPresignedProfileUrl(
            user.profilePicture
          );

          if (!isMounted) return;

          setAvatarUrl(res.imageUrl);
          setUploadedS3Key(user.profilePicture);
        } else {
          setAvatarUrl(profile);
          setUploadedS3Key("");
        }

        setFullName(`${user.firstName} ${user.lastName}`);
        completedUserRequestRef.current = requestKey;
      } catch (err) {
        if (!isMounted) return;
      } finally {
        if (isMounted && pendingUserRequestRef.current === requestKey) {
          pendingUserRequestRef.current = null;
        }
      }
    };

    fetchUser();

    return () => {
      isMounted = false;
      if (pendingUserRequestRef.current) {
        pendingUserRequestRef.current = null;
      }
    };
  }, [role, passedUserId, authUser?.userId]);

  const handleOrgChange = async (orgId: string) => {
    setSelectedOrg(orgId);
    setSelectedLocation("");
    setSelectedDept("");
    setDepartments([]);

    const org = organizations.find((o) => o.orgId === orgId);

    setUserData((prev) => ({
      ...prev,
      orgId,
      orgName: org?.orgName || "",
    }));

    try {
      const locs = await assetRepository.getLocations(orgId);
      setLocations(locs);
    } catch (err) {
    }
  };

  const handleLocationChange = (locId: string) => {
    setSelectedLocation(locId);
    setSelectedDept("");

    const loc = locations.find((l) => l.locationId === locId);

    setUserData((prev) => ({
      ...prev,
      locationId: locId,
      location: loc?.location || "",
    }));

    setDepartments(loc?.departments || []);
  };

  const handleDeptChange = (deptId: string) => {
    setSelectedDept(deptId);
    const dept = departments.find((d) => d.departmentId === deptId);

    setUserData((prev) => ({
      ...prev,
      departmentId: deptId,
      departmentName: dept?.departmentName || "",
    }));
  };

  const handleVendorChange = (vendorId: string) => {
    setSelectedVendor(vendorId);
    const vendor = vendors.find((v) => v.vendorId === vendorId);
    setUserData((prev) => ({
      ...prev,
      vendorId: vendorId,
    }));
  };

  const handleFieldChange = (field: keyof typeof updateUserSchema.shape, value: string) => {
    setUserData({ ...userData, [field]: value });

    const fieldSchema = updateUserSchema.shape[field];
    if (fieldSchema) {
      const result = fieldSchema.safeParse(value);
      setErrors((prev) => ({
        ...prev,
        [field]: result.success ? "" : result.error.issues[0].message,
      }));
    }
  };

  // Allow all roles to update their own info
  const handleSaveChanges = async () => {
    if (!userId) return;

    try {
      updateUserSchema.parse(userData);

      // Only Admin can update others
      const targetRole = userData.userRole;

      const canEdit =
        role === "Admin" ||
        userId === loggedInUserId ||
        (role === "OrgAdmin" &&
          ["LocationAdmin", "DeptUser"].includes(targetRole)) ||
        (role === "LocationAdmin" &&
          targetRole === "DeptUser");

      if (!canEdit) {
        setCenterAlertMessage(
          "You don't have permission to update this user."
        );
        setCenterAlertStatus("error");
        setCenterAlertVisible(true);
        return;
      }

      let payload = { ...userData };

      if (userData.userRole === "Vendor" || userData.userRole === "VendorUser") {
        payload = {
          ...payload,
          orgId: userData.orgId || payload.orgId,
          locationId: userData.locationId || payload.locationId,
          departmentId: userData.departmentId || payload.departmentId,
          orgName: userData.orgName || payload.orgName,
          location: userData.location || payload.location,
          departmentName: userData.departmentName || payload.departmentName,
        };
      }

      await authRepository.updateUser(userId, payload);

      setFullName(`${userData.firstName} ${userData.lastName}`);
      setCenterAlertMessage("Your profile information has been saved.");
      setCenterAlertStatus("success");
      setCenterAlertVisible(true);

    } catch (error: any) {
      if (error instanceof z.ZodError) {
        const newErrors = flattenZodErrors(error);
        setErrors(newErrors);
        setCenterAlertMessage("Please fix the errors before saving.");
      } else {
        const errorMessage =
          typeof error?.response?.data === "string"
            ? error.response.data
            : error?.response?.data?.message ||
            "There was an error updating your profile.";

        setCenterAlertMessage(errorMessage);
      }

      setCenterAlertStatus("error");
      setCenterAlertVisible(true);
    }
  };
  const handleToggleUserStatus = async () => {
    if (!userId || role !== "Admin") return;

    try {
      const updatedStatus = !userData.isActive;

      await authRepository.updateUser(userId, {
        ...userData,
        isActive: updatedStatus,
      });

      setUserData(prev => ({
        ...prev,
        isActive: updatedStatus,
      }));

      setCenterAlertMessage(
        updatedStatus ? "User activated successfully." : "User deactivated successfully."
      );
      setCenterAlertStatus("success");
      setCenterAlertVisible(true);
    } catch (error) {
      setCenterAlertMessage("Failed to update user status.");
      setCenterAlertStatus("error");
      setCenterAlertVisible(true);
    }
  };


  const handleDeleteUser = async () => {
    if (!userId) return;

    try {
      await authRepository.deleteUser(userId);

      if (role === "Admin") {
        if (loggedInUserId === userId) {

          setCenterAlertMessage("Admin cannot delete their own account.");
          setCenterAlertStatus("error");
          setCenterAlertVisible(true);
          onClose();
          return;
        } else {

          setCenterAlertMessage("User has been deleted successfully.");
          setCenterAlertStatus("success");
          setCenterAlertVisible(true);
          onClose();

          setTimeout(() => {
            navigate("/admin/UserList");
          }, 1200);
        }
      }
    } catch {
      setCenterAlertMessage("Failed to delete user.");
      setCenterAlertStatus("error");
      setCenterAlertVisible(true);
    }
  };

  const handlePasswordFieldChange = (field: keyof typeof formData, value: string) => {
    setFormData({ ...formData, [field]: value });

    const fieldSchema = passwordSchema.shape[field];
    if (fieldSchema) {
      const result = fieldSchema.safeParse(value);
      setErrors((prev) => ({
        ...prev,
        [field]: result.success ? "" : result.error.issues[0].message,
      }));
    }
  };

  const handleChangePassword = async () => {
    try {
      passwordSchema.parse(formData);

      const token = localStorage.getItem("jwt");
      if (!token) throw new Error("Please log in again.");

      const decoded: any = jwtDecode(token);
      const email =
        decoded["http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name"];
      if (!email) throw new Error("Could not extract email from token.");

      await authRepository.changePassword({
        email,
        oldPassword: formData.oldPassword,
        newPassword: formData.newPassword,
      });

      setCenterAlertMessage("Your password has been successfully updated.");
      setCenterAlertStatus("success");
      setCenterAlertVisible(true);
      setFormData({ oldPassword: "", newPassword: "", confirmPassword: "" });
    } catch (error) {
      if (error instanceof z.ZodError) {
        const newErrors = flattenZodErrors(error);
        setErrors(newErrors);
        setCenterAlertMessage("Please fix the errors before submitting.");
      } else {
        setCenterAlertMessage("Check your old password and try again.");
      }
      setCenterAlertStatus("error");
      setCenterAlertVisible(true);
    }
  };

  const handleUploadProfile = async () => {
    if (!userId || !selectedFile) return;

    setIsUploading(true);

    try {
      const updatedUser = await authRepository.uploadUserProfile(userId, selectedFile);

      if (updatedUser.profilePicture) {
        //const res = await authRepository.getPresignedProfileUrl(updatedUser.profilePicture);

        setAvatarUrl(updatedUser.imageUrl);
        setUploadedS3Key(updatedUser.profilePicture);
        setUser(updatedUser);
      } else {
        setAvatarUrl(profile);
        setUploadedS3Key("");
      }

      setSelectedFile(null);

      setCenterAlertMessage("Profile picture updated successfully.");
      setCenterAlertStatus("success");
      setCenterAlertVisible(true);
    } catch (err) {
      setCenterAlertMessage("Failed to upload profile picture.");
      setCenterAlertStatus("error");
      setCenterAlertVisible(true);
    } finally {
      setIsUploading(false);
    }
  };


  const handleDeleteProfile = async () => {
    if (!userId || !uploadedS3Key) return;

    setIsDeletingPic(true);

    try {
      await authRepository.deleteUserProfile(userId, uploadedS3Key);

      setCenterAlertMessage("Profile picture removed.");
      setCenterAlertStatus("success");
      setCenterAlertVisible(true);
    } catch (err) {
      setCenterAlertMessage("Failed to delete profile picture.");
      setCenterAlertStatus("error");
      setCenterAlertVisible(true);
    } finally {
      setIsDeletingPic(false);
    }
  };

  return (
    <Box>
      {/* Center alert */}
      {centerAlertVisible && (
        <Center
          position="fixed"
          top="0"
          left="0"
          right="0"
          bottom="0"
          zIndex={60}
        >
          <Alert
            status={centerAlertStatus}
            variant="solid"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            width="600px"
            borderRadius="14px"
            boxShadow="xl"
            py={10}
            px={16}
          >
            <AlertIcon boxSize="40px" mr={0} />
            <Text fontSize="lg" fontWeight="700" mt={1}>
              {centerAlertMessage}
            </Text>
          </Alert>
        </Center>
      )}

      <SimpleGrid
        mb="20px"
        columns={{ sm: 1, lg: 2 }}
        spacing={{ base: "20px", xl: "20px" }}
      >
        <Flex direction="column">
          {/* Profile Card */}
          <Card mt="80px" mb="20px" alignItems="center" marginLeft="20px" paddingRight="10px">
            <Button
              variant="ghost"
              float={"left"}
              marginRight="400px"
              colorScheme="gray"
              leftIcon={<ArrowBackIcon width="20px" height="20px" />}
              onClick={() => navigate("/admin/UserList")}
            >
              Back to list
            </Button>
            <Flex
              w="100%"
              bgGradient="linear(to-b, brand.400, brand.600)"
              minH="127px"
              borderRadius="16px"
            ></Flex>
            <Avatar
              mx="auto"
              src={avatarUrl}
              h="87px"
              w="87px"
              mt="-43px"
              mb="15px"
            />
            <Text fontSize="2xl" textColor={textColorPrimary} fontWeight="700">
              {fullName}
            </Text>
          </Card>

          {/* Left: Account Settings */}

          <Card marginLeft="20px" paddingRight="10px">
            <Flex direction="column" mb="40px" ms="10px">
              <Text fontSize="xl" color={textColorPrimary} fontWeight="bold">
                Account Settings
              </Text>
              <Text fontSize="md" color={textColorSecondary}>
                Here you can change user account information
              </Text>
            </Flex>

            <SimpleGrid columns={{ sm: 1, md: 2 }} spacing="20px" mb="25px">
              {/* Fields */}
              <FormControl isInvalid={!!errors.firstName}>
                <InputField
                  id="firstName"
                  label="First Name"
                  value={userData.firstName}
                  onChange={(e: any) => handleFieldChange("firstName", e.target.value)}
                />
                <FormErrorMessage>{errors.firstName}</FormErrorMessage>
              </FormControl>

              <FormControl isInvalid={!!errors.lastName}>
                <InputField
                  id="lastName"
                  label="Last Name"
                  value={userData.lastName}
                  onChange={(e: any) => handleFieldChange("lastName", e.target.value)}
                />
                <FormErrorMessage>{errors.lastName}</FormErrorMessage>
              </FormControl>

              <FormControl isInvalid={!!errors.userRole}>
                <FormLabel>Select Role</FormLabel>
                <Select
                  id="role"
                  value={userData.userRole}
                  onChange={(e) => handleFieldChange("userRole", e.target.value)}
                  isDisabled={role !== "Admin"}
                  color="white"
                  sx={{
                    option: {
                      color: "white",
                      background: "#1B254B",
                    },
                  }}
                >
                  <option value="Admin">Admin</option>
                  <option value="OrgAdmin">OrgAdmin</option>
                  <option value="DeptUser">DeptUser</option>
                  <option value="Vendor">Vendor</option>
                  <option value="LocationAdmin">LocationAdmin</option>
                  <option value="VendorUser">VendorUser</option>
                </Select>
                <FormErrorMessage>{errors.userRole}</FormErrorMessage>
              </FormControl>

              {(
                userData.userRole === "Vendor" ||
                userData.userRole === "VendorUser"
              ) && (
                  <FormControl>
                    <FormLabel>Vendor</FormLabel>
                    <Select
                      value={userData.vendorId || ""}
                      onChange={(e) => handleVendorChange(e.target.value)}
                      isDisabled={role !== "Admin"}
                      color="white"
                      sx={{
                        option: {
                          color: "white",
                          background: "#1B254B",
                        },
                      }}
                    >
                      <option value="">Select Vendor</option>

                      {vendors.map((vendor) => (
                        <option key={vendor.vendorId} value={vendor.vendorId}>
                          {vendor.name}
                        </option>
                      ))}
                    </Select>
                  </FormControl>
                )}

              <InputField
                id="email"
                label="Email"
                value={userData.email}
                isReadOnly
              />
              <FormControl isInvalid={!!errors.contactNumber && touched.contactNumber}>
                <InputField
                  id="contactNumber"
                  label="Contact Number"
                  value={userData.contactNumber}
                  onChange={(e: any) => handleFieldChange("contactNumber", e.target.value)}
                  onBlur={() =>
                    setTouched((prev) => ({ ...prev, contactNumber: true }))
                  }
                />
                {errors.contactNumber && touched.contactNumber && (
                  <FormErrorMessage>{errors.contactNumber}</FormErrorMessage>
                )}
              </FormControl>
              <>
                {/* Organization */}
                {userData.userRole !== "Vendor" && userData.userRole !== "VendorUser" && userData.userRole !== "Admin" && (
                  <FormControl>
                    <FormLabel>Organization</FormLabel>

                    {isDeptUser || isSelf || isOrgAdmin ? (
                      <InputField value={userData.orgName} isReadOnly />
                    ) : (
                      <Select
                        value={selectedOrg}
                        onChange={(e) => handleOrgChange(e.target.value)}
                        isDisabled={isSelf}
                        color="white"
                        sx={{
                          option: {
                            color: "white",
                            background: "#1B254B",
                          },
                        }}
                      >
                        <option value="">Select Organization</option>

                        {organizations.map((org) => (
                          <option key={org.orgId} value={org.orgId}>
                            {org.orgName}
                          </option>
                        ))}
                      </Select>
                    )}
                  </FormControl>
                )}

                {/* Plant */}
                {userData.userRole !== "Vendor" && userData.userRole !== "VendorUser" && userData.userRole !== "Admin" && userData.userRole !== "LeasedOrg" && userData.userRole !== "OrgAdmin" && (
                  <FormControl>
                    <FormLabel>Plant</FormLabel>

                    {isDeptUser || isSelf ? (
                      <InputField value={userData.location} isReadOnly />
                    ) : (
                      <Select
                        value={selectedLocation}
                        onChange={(e) => handleLocationChange(e.target.value)}
                        isDisabled={isSelf || (!isAdmin && !isOrgAdmin)}
                        color="white"
                        sx={{
                          option: {
                            color: "white",
                            background: "#1B254B",
                          },
                        }}
                      >
                        <option value="">Select Plant</option>

                        {locations.map((loc) => (
                          <option key={loc.locationId} value={loc.locationId}>
                            {loc.location}
                          </option>
                        ))}
                      </Select>
                    )}
                  </FormControl>
                )}

                {/* Department */}
                {userData.userRole !== "Vendor" && userData.userRole !== "VendorUser" && userData.userRole !== "Admin" && userData.userRole !== "OrgAdmin" && userData.userRole !== "LocationAdmin" && userData.userRole !== "LeasedOrg" && (
                  <FormControl>
                    <FormLabel>Department</FormLabel>

                    {isDeptUser ? (
                      <InputField
                        value={userData.departmentName}
                        isReadOnly
                      />
                    ) : (
                      <Select
                        value={selectedDept}
                        onChange={(e) => handleDeptChange(e.target.value)}
                        isDisabled={
                          !selectedLocation ||
                          isSelf ||
                          (!isAdmin && !isOrgAdmin && !isLocationAdmin)
                        }
                        color="white"
                        sx={{
                          option: {
                            color: "white",
                            background: "#1B254B",
                          },
                        }}
                      >
                        <option value="">Select Department</option>

                        {departments.map((dept) => (
                          <option key={dept.departmentId} value={dept.departmentId}>
                            {dept.departmentName}
                          </option>
                        ))}
                      </Select>
                    )}
                  </FormControl>
                )}
              </>
            </SimpleGrid>

            <Flex gap="10px" paddingRight={12} >
              {/* {role === "Admin" && ( */}
              {role === "Admin" && userId !== loggedInUserId && (

                <Button
                  minW="170px"
                  fontSize="sm"
                  fontWeight="500"
                  colorScheme={userData.isActive ? "orange" : "green"}
                  onClick={handleToggleUserStatus}
                >
                  {userData.isActive ? "Deactivate User" : "Activate User"}
                </Button>
              )}

              <Button
                variant="brand"
                minW="170px"
                fontSize="sm"
                fontWeight="500"
                onClick={handleSaveChanges}
              >
                Save changes
              </Button>

              {role === "Admin" && userId !== loggedInUserId && (
                <Button
                  minW="170px"
                  fontSize="sm"
                  fontWeight="500"
                  colorScheme="red"
                  onClick={onOpen}
                >
                  Delete User
                </Button>
              )}

            </Flex>
          </Card>

        </Flex>

        {/* Right: Password */}

        <Flex direction="column">
          {isSelf && (
            <Card mt="80px" marginLeft="10px">
              <Flex direction="column" mb="40px" ms="10px">
                <Text fontSize="xl" color={textColorPrimary} fontWeight="bold">
                  Change password
                </Text>
                <Text fontSize="md" color={textColorSecondary}>
                  Here you can set your new password
                </Text>
              </Flex>

              <FormControl isInvalid={!!errors.oldPassword}>
                <InputField
                  id="oldPassword"
                  label="Old Password"
                  type="password"
                  value={formData.oldPassword}
                  onChange={(e: any) => handlePasswordFieldChange("oldPassword", e.target.value)}
                />
                <FormErrorMessage>{errors.oldPassword}</FormErrorMessage>
              </FormControl>

              <FormControl isInvalid={!!errors.newPassword}>
                <InputField
                  id="newPassword"
                  label="New Password"
                  type="password"
                  value={formData.newPassword}
                  onChange={(e: any) => handlePasswordFieldChange("newPassword", e.target.value)}
                />
                <FormErrorMessage>{errors.newPassword}</FormErrorMessage>
              </FormControl>

              <FormControl isInvalid={!!errors.confirmPassword}>
                <InputField
                  id="confirmPassword"
                  label="Confirm Password"
                  type="password"
                  value={formData.confirmPassword}
                  onChange={(e: any) => handlePasswordFieldChange("confirmPassword", e.target.value)}
                />
                <FormErrorMessage>{errors.confirmPassword}</FormErrorMessage>
              </FormControl>

              <Button
                variant="brand"
                minW="183px"
                fontSize="sm"
                fontWeight="500"
                ms="auto"
                onClick={handleChangePassword}
              >
                Change Password
              </Button>
            </Card>
          )}

          {/* Profile Picture Upload Card */}
          {isSelf && (
            <Card mt="20px" marginLeft="10px">
              <Flex direction="column" mb="40px" ms="10px">
                <Text fontSize="xl" color={textColorPrimary} fontWeight="bold">
                  Profile Picture Upload
                </Text>

                <Text fontSize="md" color={textColorSecondary}>
                  Upload a new profile picture here
                </Text>
              </Flex>

              <Center mb="20px">
                <Avatar src={avatarUrl} h="87px" w="87px" borderRadius="full" />
              </Center>

              <FormControl>
                <FormLabel>Select Image</FormLabel>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      setSelectedFile(file);
                      const preview = URL.createObjectURL(file);
                      setAvatarUrl(preview);
                    }
                  }}
                />
              </FormControl>

              <Flex mt="20px" gap={4} justify="flex-end">
                <Button
                  variant="brand"
                  minW="183px"
                  fontSize="sm"
                  fontWeight="500"
                  onClick={handleUploadProfile}
                  isDisabled={!selectedFile || isUploading}
                  isLoading={isUploading}
                >
                  Upload Picture
                </Button>

                {/* if want to show the delete button only for Admin */}
                {(role === "Admin") && (
                  <Button
                    minW="183px"
                    fontSize="sm"
                    fontWeight="500"
                    colorScheme="red"
                    onClick={handleDeleteProfile}
                    isDisabled={!uploadedS3Key || isDeletingPic}
                    isLoading={isDeletingPic}
                  >
                    Remove Picture
                  </Button>
                )}
              </Flex>
            </Card>
          )}
        </Flex>
      </SimpleGrid>

      {/* Delete Confirmation */}
      <AlertDialog
        isOpen={isOpen}
        leastDestructiveRef={cancelRef}
        onClose={onClose}
        isCentered
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              {role === "Admin"
                ? loggedInUserId === userId
                  ? "Delete Account"
                  : "Delete User"
                : "Delete Account"}
            </AlertDialogHeader>
            <AlertDialogBody>
              {role === "Admin"
                ? loggedInUserId === userId
                  ? "Are you sure you want to delete your account? This action cannot be undone."
                  : "Are you sure you want to delete this user? This action cannot be undone."
                : "Are you sure you want to delete your account? This action cannot be undone."}
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onClose}>
                Cancel
              </Button>
              <Button colorScheme="red" onClick={handleDeleteUser} ml={3}>
                Delete
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
}
